import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

/**
 * Themed replacements for window.confirm / prompt / alert, shared by every web app. Each helper returns a promise and
 * mounts its own host on first use, so nothing needs wiring into an app root. Colours come from the suite tokens
 * (--card, --border, --primary ...), so the dialogs follow the palette and dark mode.
 */

export interface DialogButton {
    id: string;
    label: string;
    kind?: "primary" | "danger" | "default";
}

interface Request {
    key: number;
    title: string;
    message?: string;
    buttons: DialogButton[];
    /** The button Esc and a click outside choose. */
    cancelId: string;
    input?: { label?: string; value?: string; placeholder?: string; multiline?: boolean };
    resolve: (r: { id: string; value: string }) => void;
}

let seq = 0;
let host: { push: (r: Request) => void } | null = null;
const waiting: Request[] = [];

function Host() {
    const [queue, setQueue] = useState<Request[]>([]);
    useEffect(() => {
        host = { push: (r) => setQueue((q) => [...q, r]) };
        for (const r of waiting.splice(0)) host.push(r);
        return () => { host = null; };
    }, []);
    const cur = queue[0];
    return cur ? <Box key={cur.key} req={cur} done={(id, value) => { cur.resolve({ id, value }); setQueue((q) => q.slice(1)); }} /> : null;
}

function Box({ req, done }: { req: Request; done: (id: string, value: string) => void }) {
    const [value, setValue] = useState(req.input?.value ?? "");
    const boxRef = useRef<HTMLDivElement>(null);
    const primary = req.buttons.find((b) => b.kind === "primary") ?? req.buttons[req.buttons.length - 1]!;
    useEffect(() => {
        const prev = document.activeElement as HTMLElement | null;
        const first = boxRef.current?.querySelector<HTMLElement>(req.input ? "input,textarea" : "button[data-primary]");
        first?.focus();
        if (first instanceof HTMLInputElement) first.select();
        return () => prev?.focus?.();
    }, [req.input]);
    const key = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") { e.stopPropagation(); done(req.cancelId, value); }
        else if (e.key === "Enter" && !(e.target instanceof HTMLTextAreaElement) && !(e.target instanceof HTMLButtonElement)) { e.preventDefault(); done(primary.id, value); }
        else if (e.key === "Tab") {
            const items = Array.from(boxRef.current?.querySelectorAll<HTMLElement>("button,input,textarea") ?? []);
            if (!items.length) return;
            const i = items.indexOf(document.activeElement as HTMLElement);
            const next = e.shiftKey ? (i <= 0 ? items.length - 1 : i - 1) : i === items.length - 1 ? 0 : i + 1;
            e.preventDefault();
            items[next]!.focus();
        }
    };
    const hsl = (v: string) => `hsl(var(${v}))`;
    const btn = (b: DialogButton): React.CSSProperties => ({
        height: 32, padding: "0 16px", borderRadius: 4, fontSize: 14, cursor: "pointer",
        border: `1px solid ${b.kind === "primary" ? hsl("--primary") : b.kind === "danger" ? hsl("--destructive") : hsl("--border")}`,
        background: b.kind === "primary" ? hsl("--primary") : b.kind === "danger" ? hsl("--destructive") : hsl("--card"),
        color: b.kind === "primary" ? hsl("--primary-foreground") : b.kind === "danger" ? hsl("--destructive-foreground") : hsl("--foreground")
    });
    return (
        <div
            style={{ position: "fixed", inset: 0, zIndex: 100000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.45)" }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) done(req.cancelId, value); }}
            onKeyDown={key}
        >
            <div
                ref={boxRef}
                role="alertdialog"
                aria-modal="true"
                aria-label={req.title}
                style={{ width: "min(440px, calc(100vw - 32px))", borderRadius: 8, padding: 20, background: hsl("--card"), color: hsl("--card-foreground"), border: `1px solid ${hsl("--border")}`, boxShadow: "0 16px 48px rgba(0,0,0,0.35)", font: "14px/1.45 'Segoe UI', system-ui, sans-serif" }}
            >
                <div style={{ fontSize: 18, fontWeight: 600, marginBottom: req.message || req.input ? 8 : 16 }}>{req.title}</div>
                {req.message ? <div style={{ marginBottom: 14, color: hsl("--muted-foreground"), whiteSpace: "pre-wrap" }}>{req.message}</div> : null}
                {req.input ? (
                    <label style={{ display: "block", marginBottom: 16 }}>
                        {req.input.label ? <span style={{ display: "block", marginBottom: 4 }}>{req.input.label}</span> : null}
                        {req.input.multiline ? (
                            <textarea value={value} rows={4} placeholder={req.input.placeholder} onChange={(e) => setValue(e.target.value)} style={{ width: "100%", padding: "6px 8px", borderRadius: 4, border: `1px solid ${hsl("--input")}`, background: hsl("--background"), color: hsl("--foreground") }} />
                        ) : (
                            <input value={value} placeholder={req.input.placeholder} onChange={(e) => setValue(e.target.value)} style={{ width: "100%", height: 32, padding: "0 8px", borderRadius: 4, border: `1px solid ${hsl("--input")}`, background: hsl("--background"), color: hsl("--foreground") }} />
                        )}
                    </label>
                ) : null}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                    {req.buttons.map((b) => (
                        <button key={b.id} type="button" data-primary={b === primary ? "" : undefined} style={btn(b)} onClick={() => done(b.id, value)}>{b.label}</button>
                    ))}
                </div>
            </div>
        </div>
    );
}

let mounted = false;
function ensureHost() {
    if (mounted || typeof document === "undefined") return;
    mounted = true;
    const el = document.createElement("div");
    el.setAttribute("data-fg-dialogs", "");
    document.body.appendChild(el);
    createRoot(el).render(<Host />);
}

/** Shows a dialog and resolves with the id of the button chosen (and the input's text, if it has one). */
export function showDialog(o: Omit<Request, "key" | "resolve">): Promise<{ id: string; value: string }> {
    ensureHost();
    return new Promise((resolve) => {
        const r: Request = { ...o, key: ++seq, resolve };
        if (host) host.push(r);
        else waiting.push(r);
    });
}

export interface ConfirmOptions {
    title: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** A destructive action: the confirm button is red. */
    danger?: boolean;
}

/** Themed window.confirm. */
export async function confirmDialog(o: ConfirmOptions): Promise<boolean> {
    const r = await showDialog({
        title: o.title,
        message: o.message,
        cancelId: "cancel",
        buttons: [{ id: "cancel", label: o.cancelLabel ?? "Cancel" }, { id: "ok", label: o.confirmLabel ?? "OK", kind: o.danger ? "danger" : "primary" }]
    });
    return r.id === "ok";
}

/** Themed window.prompt: the text, or null when cancelled. */
export async function promptDialog(o: { title: string; message?: string; label?: string; value?: string; placeholder?: string; multiline?: boolean; confirmLabel?: string }): Promise<string | null> {
    const r = await showDialog({
        title: o.title,
        message: o.message,
        cancelId: "cancel",
        input: { label: o.label, value: o.value, placeholder: o.placeholder, multiline: o.multiline },
        buttons: [{ id: "cancel", label: "Cancel" }, { id: "ok", label: o.confirmLabel ?? "OK", kind: "primary" }]
    });
    return r.id === "ok" ? r.value : null;
}

/** Themed window.alert. */
export async function alertDialog(o: { title: string; message?: string; okLabel?: string }): Promise<void> {
    await showDialog({ title: o.title, message: o.message, cancelId: "ok", buttons: [{ id: "ok", label: o.okLabel ?? "OK", kind: "primary" }] });
}

export type UnsavedChoice = "save" | "discard" | "cancel";

/** The one prompt for leaving a document with unsaved changes: Save, Don't save or Cancel. */
export async function unsavedChangesDialog(fileName?: string): Promise<UnsavedChoice> {
    const r = await showDialog({
        title: "Do you want to save your changes?",
        message: fileName ? `${fileName} has changes that are not saved yet.` : "This document has changes that are not saved yet.",
        cancelId: "cancel",
        buttons: [{ id: "cancel", label: "Cancel" }, { id: "discard", label: "Don't save" }, { id: "save", label: "Save", kind: "primary" }]
    });
    return r.id as UnsavedChoice;
}
